class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') {
        this.close();
      }
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.setHeaderCartIconAccessibility();

    /*
     * Shopify replaces parts of the drawer after cart changes.
     * Reapply the mobile layout whenever that happens.
     */
    this.drawerObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        this.setupMobileLayout();
      });
    });

    const drawer = this.querySelector('#CartDrawer');

    if (drawer) {
      this.drawerObserver.observe(drawer, {
        childList: true,
        subtree: true
      });
    }

    this.resizeHandler = () => {
      this.setupMobileLayout();
    };

    window.addEventListener(
      'resize',
      this.resizeHandler,
      { passive: true }
    );

    window.addEventListener(
      'orientationchange',
      this.resizeHandler,
      { passive: true }
    );
  }

  disconnectedCallback() {
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }

    window.removeEventListener(
      'resize',
      this.resizeHandler
    );

    window.removeEventListener(
      'orientationchange',
      this.resizeHandler
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!cartLink) return;

    cartLink.setAttribute(
      'role',
      'button'
    );

    cartLink.setAttribute(
      'aria-haspopup',
      'dialog'
    );

    // Click handling for this link lives in an inline script in
    // sections/header.liquid so the drawer can intercept the click the
    // instant the page paints, without waiting on this file to finish
    // loading. This method still owns role/aria attrs + keyboard support.

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          event.preventDefault();

          this.open(cartLink);
        }
      }
    );
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(
        triggeredBy
      );
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute(
        'role'
      )
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    requestAnimationFrame(() => {
      this.classList.add(
        'animate',
        'active'
      );

      const cartDrawerItems =
        this.querySelector(
          'cart-drawer-items'
        );

      if (cartDrawerItems) {
        if (
          typeof cartDrawerItems
            .updateCartUpsellToggleState ===
          'function'
        ) {
          cartDrawerItems.updateCartUpsellToggleState();
        }

        if (
          typeof cartDrawerItems
            .updateCartUpsellVisibility ===
          'function'
        ) {
          cartDrawerItems.updateCartUpsellVisibility();
        }
      }

      this.setupMobileLayout();
    });

    /*
     * Run again after drawer animation/layout.
     */
    setTimeout(() => {
      this.setupMobileLayout();
    }, 50);

    setTimeout(() => {
      this.setupMobileLayout();
    }, 200);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains(
            'is-empty'
          )
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus ===
            'function'
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }

        this.setupMobileLayout();
      },
      {
        once: true
      }
    );

    /*
     * Lock the page behind the cart.
     */
    document.body.classList.add(
      'overflow-hidden'
    );
  }

  close() {
    this.classList.remove(
      'active'
    );

    if (
      typeof removeTrapFocus ===
      'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );

    this.clearMobileLayout();
  }

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote
          .nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape ===
        'function'
    ) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  /*
   * PRODUCT PAGE ADD TO CART
   *
   * Your Buy Box already adds:
   *
   * Bowl
   * +
   * correct free duck quantity
   *
   * in one request.
   *
   * Therefore we simply render the
   * completed Shopify sections.
   */
  renderContents(parsedState) {
    this.productId =
      parsedState.id;

    this.classList.remove(
      'is-empty'
    );

    const inner =
      this.querySelector(
        '.drawer__inner'
      );

    if (inner) {
      inner.classList.remove(
        'is-empty'
      );
    }

    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        const html =
          parsedState.sections?.[
            section.id
          ];

        if (
          !sectionElement ||
          !html
        ) {
          return;
        }

        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            html,
            section.selector
          );
      }
    );

    const overlay =
      this.querySelector(
        '#CartDrawer-Overlay'
      );

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.open();

    requestAnimationFrame(() => {
      this.setupMobileLayout();
    });

    setTimeout(() => {
      this.setupMobileLayout();
    }, 100);
  }

  /*
   * ========================================================
   * MOBILE SCROLL
   * ========================================================
   *
   * IMPORTANT:
   *
   * We are NOT using .tp-cart-main anymore.
   *
   * Your actual DOM doesn't contain it.
   *
   * Instead:
   *
   * drawer__inner = scroll container
   * header        = sticky top
   * footer        = sticky bottom
   *
   * This is much harder for theme CSS to break.
   */
  setupMobileLayout() {
    const isMobile =
      window.matchMedia(
        '(max-width: 749px)'
      ).matches;

    if (!isMobile) {
      this.clearMobileLayout();
      return;
    }

    const inner =
      this.querySelector(
        '.drawer__inner'
      );

    const header =
      this.querySelector(
        '.drawer__header'
      );

    const items =
      this.querySelector(
        'cart-drawer-items'
      );

    const footer =
      this.querySelector(
        '.drawer__inner > .drawer__footer'
      );

    if (!inner) return;

    /*
     * =========================================
     * DRAWER ITSELF SCROLLS
     * =========================================
     */

    inner.style.setProperty(
      'height',
      '100dvh',
      'important'
    );

    inner.style.setProperty(
      'max-height',
      '100dvh',
      'important'
    );

    inner.style.setProperty(
      'min-height',
      '0',
      'important'
    );

    inner.style.setProperty(
      'display',
      'flex',
      'important'
    );

    inner.style.setProperty(
      'flex-direction',
      'column',
      'important'
    );

    inner.style.setProperty(
      'overflow-x',
      'hidden',
      'important'
    );

    inner.style.setProperty(
      'overflow-y',
      'auto',
      'important'
    );

    inner.style.setProperty(
      '-webkit-overflow-scrolling',
      'touch',
      'important'
    );

    inner.style.setProperty(
      'overscroll-behavior-y',
      'contain',
      'important'
    );

    inner.style.setProperty(
      'touch-action',
      'pan-y',
      'important'
    );

    /*
     * =========================================
     * HEADER STAYS AT TOP
     * =========================================
     */

    if (header) {
      header.style.setProperty(
        'position',
        'sticky',
        'important'
      );

      header.style.setProperty(
        'top',
        '0',
        'important'
      );

      header.style.setProperty(
        'z-index',
        '40',
        'important'
      );

      header.style.setProperty(
        'flex',
        '0 0 auto',
        'important'
      );

      /*
       * Prevent cart cards showing
       * underneath header while scrolling.
       */
      header.style.setProperty(
        'background',
        'var(--tp-cart-bg, #FAF7F0)',
        'important'
      );
    }

    /*
     * =========================================
     * REMOVE NESTED ITEM SCROLL
     * =========================================
     *
     * There must only be ONE vertical scroller.
     *
     * Previously:
     *
     * drawer = hidden
     * items  = auto
     *
     * But items had:
     *
     * clientHeight = scrollHeight
     *
     * meaning nothing could scroll.
     *
     * Now the drawer itself handles it.
     */

    if (items) {
      items.style.setProperty(
        'display',
        'block',
        'important'
      );

      items.style.setProperty(
        'flex',
        '0 0 auto',
        'important'
      );

      items.style.setProperty(
        'height',
        'auto',
        'important'
      );

      items.style.setProperty(
        'min-height',
        '0',
        'important'
      );

      items.style.setProperty(
        'max-height',
        'none',
        'important'
      );

      items.style.setProperty(
        'overflow',
        'visible',
        'important'
      );

      items.style.setProperty(
        'touch-action',
        'auto',
        'important'
      );

      /*
       * Internal wrappers must grow normally.
       */
      [
        items.querySelector(
          '.cart-drawer__form'
        ),

        items.querySelector(
          '.drawer__contents'
        ),

        items.querySelector(
          '.drawer__cart-items-wrapper'
        ),

        items.querySelector(
          '.cart-items'
        ),

        items.querySelector(
          '.cart-items tbody'
        )
      ].forEach((element) => {
        if (!element) return;

        element.style.setProperty(
          'height',
          'auto',
          'important'
        );

        element.style.setProperty(
          'min-height',
          '0',
          'important'
        );

        element.style.setProperty(
          'max-height',
          'none',
          'important'
        );

        element.style.setProperty(
          'overflow',
          'visible',
          'important'
        );
      });
    }

    /*
     * =========================================
     * CHECKOUT FOOTER
     * =========================================
     *
     * margin-top:auto:
     * pushes footer to bottom if cart is short.
     *
     * position:sticky:
     * keeps it visible at bottom if cart is long.
     */

    if (footer) {
      footer.style.setProperty(
        'position',
        'sticky',
        'important'
      );

      footer.style.setProperty(
        'bottom',
        '0',
        'important'
      );

      footer.style.setProperty(
        'z-index',
        '35',
        'important'
      );

      footer.style.setProperty(
        'flex',
        '0 0 auto',
        'important'
      );

      footer.style.setProperty(
        'margin-top',
        'auto',
        'important'
      );

      footer.style.setProperty(
        'overflow',
        'visible',
        'important'
      );

      footer.style.setProperty(
        'max-height',
        'none',
        'important'
      );

      footer.style.setProperty(
        'background',
        'var(--tp-cart-card, #FFFFFF)',
        'important'
      );
    }
  }

  /*
   * Remove the inline mobile layout
   * when switching to desktop.
   */
  clearMobileLayout() {
    const inner =
      this.querySelector(
        '.drawer__inner'
      );

    const header =
      this.querySelector(
        '.drawer__header'
      );

    const items =
      this.querySelector(
        'cart-drawer-items'
      );

    const footer =
      this.querySelector(
        '.drawer__inner > .drawer__footer'
      );

    const removeProperties = (
      element,
      properties
    ) => {
      if (!element) return;

      properties.forEach(
        (property) => {
          element.style.removeProperty(
            property
          );
        }
      );
    };

    removeProperties(
      inner,
      [
        'height',
        'max-height',
        'min-height',
        'display',
        'flex-direction',
        'overflow-x',
        'overflow-y',
        '-webkit-overflow-scrolling',
        'overscroll-behavior-y',
        'touch-action'
      ]
    );

    removeProperties(
      header,
      [
        'position',
        'top',
        'z-index',
        'flex',
        'background'
      ]
    );

    removeProperties(
      items,
      [
        'display',
        'flex',
        'height',
        'min-height',
        'max-height',
        'overflow',
        'touch-action'
      ]
    );

    removeProperties(
      footer,
      [
        'position',
        'bottom',
        'z-index',
        'flex',
        'margin-top',
        'overflow',
        'max-height',
        'background'
      ]
    );
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsed =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsed.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : '';
  }

  getSectionsToRender() {
    return [
      {
        id:
          'cart-drawer',

        selector:
          '#CartDrawer'
      },

      {
        id:
          'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id:
          'CartDrawer',

        section:
          'cart-drawer',

        selector:
          '.drawer__inner'
      },

      {
        id:
          'cart-icon-bubble',

        section:
          'cart-icon-bubble',

        selector:
          '.shopify-section'
      }
    ];
  }
}

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}